import { Button, Group, Loader, NumberInput, TextInput } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconDownload } from "@tabler/icons-react";
import saveAs from "file-saver";
import { useMemo, useState } from "react";
import { usePostChecklistIntegrationGenerateMutation } from "../../openapi";
import { base64TilBlob } from "../app/routes/checklist/redux/checklistThunks";

export function GenerateChecklistForm() {
  const [checklistId, setChecklistId] = useState<number | "">("");
  const [signedBy, setSignedBy] = useState<string>("");
  const [generateChecklist, { isLoading }] = usePostChecklistIntegrationGenerateMutation();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const isValidId = typeof checklistId === "number" && checklistId > 0;
  const signedByTrimmed = useMemo(() => signedBy.trim(), [signedBy]);
  const isValidSignedBy = signedByTrimmed.length > 0;

  const canGenerate = !isLoading && !isDownloading && isValidId && isValidSignedBy;

  const handleGenerate = async () => {
    if (!canGenerate) return;

    setIsDownloading(true);

    try {
      const result = await generateChecklist({
        body: { checklistId, signedBy: signedByTrimmed },
      }).unwrap();

      const blob = base64TilBlob(result.base64, result.mime);
      saveAs(blob, result.filename);

      showNotification({
        title: "Suksess",
        message: "Sjekklisten er generert og lastet ned!",
        color: "green",
      });
    } catch (error) {
      showNotification({
        title: "Feil",
        message: "Kunne ikke generere sjekklisten. Prøv igjen.",
        color: "red",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Group align="flex-end">
      <NumberInput
        label="Sjekkliste-ID"
        placeholder="Angi sjekkliste-ID"
        value={checklistId}
        onChange={(value) => {
          // Mantine NumberInput kan gi string | number | null avhengig av props.
          // Vi normaliserer til number | "".
          if (value === null || value === "" || typeof value === "string") {
            const n = Number(value);
            setChecklistId(Number.isFinite(n) && n > 0 ? n : "");
            return;
          }
          setChecklistId(value);
        }}
        min={1}
        allowDecimal={false}
        hideControls
        disabled={isLoading || isDownloading}
      />

      <TextInput
        label="Signert av"
        placeholder="Navn (eller e-post)"
        value={signedBy}
        onChange={(e) => setSignedBy(e.currentTarget.value)}
        disabled={isLoading || isDownloading}
      />

      <Button
        leftSection={isLoading || isDownloading ? <Loader size="xs" /> : <IconDownload size={16} />}
        onClick={handleGenerate}
        disabled={!canGenerate}
        loading={isLoading || isDownloading}
        variant="filled"
        color="blue"
      >
        Generer og last ned sjekkliste
      </Button>
    </Group>
  );
}
